"use client";

import { c as _c } from "react/compiler-runtime";
import { useContactSalesControllerCreate } from "@dc/gateway-jsonapi";
import { addToast } from "@dc/ui/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { type Resolver, useForm } from "react-hook-form";
import { EVENTS, useAnalytics } from "@/hooks/useAnalytics";
import { useOrganization } from "@/hooks/useOrganization";
import { type ContactSalesFormValues, contactSalesSchemaFor } from "../ContactSalesModal.schema";
import { PREFILLED_MESSAGE_ORIGINS, REQUIRED_MESSAGE_ORIGINS } from "../types";
import { useContactSales } from "./useContactSales";
export function useContactSalesForm() {
  const $ = _c(36);
  const {
    close,
    origin
  } = useContactSales();
  const {
    currentOrganizationId
  } = useOrganization();
  const {
    trackEvent
  } = useAnalytics();
  const t = useTranslations("contact_sales");
  let messageRequired;
  let prefilledMessage;
  let t0;
  let t1;
  if ($[0] !== origin || $[1] !== t) {
    messageRequired = !!origin && REQUIRED_MESSAGE_ORIGINS.has(origin);
    prefilledMessage = origin && PREFILLED_MESSAGE_ORIGINS.has(origin) ? t(`prefilled_messages.${origin}`) : "";
    t1 = useForm;
    t0 = zodResolver(contactSalesSchemaFor(messageRequired));
    $[0] = origin;
    $[1] = t;
    $[2] = messageRequired;
    $[3] = prefilledMessage;
    $[4] = t0;
    $[5] = t1;
  } else {
    messageRequired = $[2];
    prefilledMessage = $[3];
    t0 = $[4];
    t1 = $[5];
  }
  const t2 = t0 as Resolver<ContactSalesFormValues>;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      message: ""
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== prefilledMessage) {
    t4 = {
      message: prefilledMessage
    };
    $[7] = prefilledMessage;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = {
      resolver: t2,
      defaultValues: t3,
      values: t4,
      mode: "onTouched"
    };
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const form = t1(t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      skipInvalidation: true
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== close || $[14] !== form || $[15] !== origin || $[16] !== t || $[17] !== trackEvent) {
    t7 = () => {
      trackEvent(EVENTS.CONTACT_SALES_SUBMITTED, {
        origin
      });
      addToast({
        title: t("toast_success"),
        color: "success"
      });
      form.reset();
      close();
    };
    $[13] = close;
    $[14] = form;
    $[15] = origin;
    $[16] = t;
    $[17] = trackEvent;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = () => {
      addToast({
        title: t("inline_error"),
        color: "danger"
      });
    };
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = {
      mutation: {
        meta: t6,
        onSuccess: t7,
        onError: t8
      }
    };
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const {
    mutate: contactSales,
    isPending
  } = useContactSalesControllerCreate(t9);
  let t10;
  if ($[24] !== contactSales || $[25] !== currentOrganizationId || $[26] !== form) {
    let t11;
    if ($[28] !== contactSales || $[29] !== currentOrganizationId) {
      t11 = values => {
        if (!currentOrganizationId) {
          return;
        }
        const trimmedMessage = values.message?.trim();
        contactSales({
          data: {
            organizationId: currentOrganizationId,
            ...(trimmedMessage ? {
              message: trimmedMessage
            } : {})
          }
        });
      };
      $[28] = contactSales;
      $[29] = currentOrganizationId;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    t10 = form.handleSubmit(t11);
    $[24] = contactSales;
    $[25] = currentOrganizationId;
    $[26] = form;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const onSubmit = t10;
  let t11;
  if ($[31] !== form || $[32] !== isPending || $[33] !== messageRequired || $[34] !== onSubmit) {
    t11 = {
      form,
      onSubmit,
      isPending,
      messageRequired
    };
    $[31] = form;
    $[32] = isPending;
    $[33] = messageRequired;
    $[34] = onSubmit;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}